import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { CartProvider } from "@/context/CartContext";
import { API } from "@/constants/brand";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { RideBooking } from "@/components/RideBooking";
import { Shop } from "@/components/Shop";
import { Services } from "@/components/Services";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminPage from "@/pages/AdminPage";
import LegalPage from "@/pages/LegalPage";
import AccountPage from "@/pages/AccountPage";
import TrackingPage from "@/pages/TrackingPage";
import DriverPortal from "@/pages/DriverPortal";
import BusinessPage from "@/pages/BusinessPage";

// Emergent Google Auth callback: exchanges the one-time session_id from the URL fragment.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const url = isAdmin ? "/auth/admin-google" : "/konto/google/session";
        const { data } = await axios.post(`${API}${url}`, { session_id: sessionId }, { withCredentials: true });
        if (!isAdmin) localStorage.setItem("konto_user", JSON.stringify(data.customer));
        toast.success(isAdmin ? "Erfolgreich angemeldet." : `Willkommen, ${data.customer.name}!`);
        navigate(location.pathname, { replace: true });
      } catch (err) {
        toast.error(err.response?.data?.detail || "Anmeldung fehlgeschlagen.");
        navigate(location.pathname, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
    </div>
  );
};

const Home = () => (
  <>
    <Navbar />
    <main>
      <Hero />
      <Marquee />
      <RideBooking />
      <Shop />
      <Services />
    </main>
    <Footer />
    <CartDrawer />
    <WhatsAppFab />
  </>
);

const WhatsAppFab = () => (
  <a
    data-testid="whatsapp-fab"
    href="https://wa.me/4915510083874?text=Hallo%20M-Line%20Solutions!%20Ich%20habe%20eine%20Frage."
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Per WhatsApp kontaktieren"
    className="fixed bottom-6 right-6 z-[60] group"
  >
    <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
    <span className="relative flex w-14 h-14 rounded-full bg-[#25D366] shadow-[0_10px_30px_-8px_rgba(37,211,102,0.55)] items-center justify-center transition-transform duration-300 group-hover:scale-110">
      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
      </svg>
    </span>
  </a>
);

function AppRouter() {
  const location = useLocation();
  // Anonymous visitor stats: no cookies, no IPs stored (GDPR-friendly)
  useEffect(() => {
    axios.post(`${API}/stats/track`, { path: location.pathname }).catch(() => {});
  }, [location.pathname]);
  // Emergent OAuth return: session_id arrives in the URL fragment — handle it BEFORE any route/auth check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>      <Route path="/" element={<Home />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/konto" element={<AccountPage />} />
      <Route path="/verfolgung" element={<TrackingPage />} />
      <Route path="/fahrer/:token" element={<DriverPortal />} />
      <Route path="/geschaeftskunden" element={<BusinessPage />} />
      <Route path="/impressum" element={<LegalPage kind="impressum" />} />
      <Route path="/datenschutz" element={<LegalPage kind="datenschutz" />} />
      <Route path="/agb" element={<LegalPage kind="agb" />} />
      <Route path="/widerruf" element={<LegalPage kind="widerruf" />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09 });
    window.__lenis = lenis;
    return () => {
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-right" richColors />
      </CartProvider>
    </div>
  );
}

export default App;
